// src/views/pages/analytics/Notes.js

import React, { useEffect, useMemo, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CSpinner,
  CFormInput,
  CBadge,
  CButton,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
} from "@coreui/react";
import DataTable from "src/components/DataTable";
import { getNotes } from "src/services/Analytics";
import SalesPicker from "src/components/SalesPicker";
import ls from "local-storage";
import { format } from "date-fns";
import { DateRangePicker } from "react-date-range";
import { subMonths } from "date-fns";
import DownloadExcel from "src/reusables/DownloadExcel";
import { Box } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePaginationState } from "src/reusables/usePaginationState";
import formatter from "src/reusables/formatter";

function VisitationNotes() {
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [search, setSearch] = useState("");
  const [showDatePicker, setShowDatePicker] = useState(false);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // --- Initialize from URL params ---
  const initialStartDate = searchParams.get("startDate")
    ? new Date(searchParams.get("startDate"))
    : subMonths(new Date(), 1);
  const initialEndDate = searchParams.get("endDate")
    ? new Date(searchParams.get("endDate"))
    : new Date();

  // Filters from URL or localStorage fallback
  const initialSmdId = searchParams.get("smdId") || (ls.get("smd")?.id && ls.get("smd").id !== "999" && ls.get("smd").value !== "all" ? ls.get("smd").id : "0");
  const initialProvinceId = searchParams.get("provinceId") || (ls.get("prov")?.id && ls.get("prov").id !== "999" && ls.get("prov").value !== "all" ? ls.get("prov").id : "0");
  const initialCityId = searchParams.get("cityId") || (ls.get("city")?.id && ls.get("city").id !== "999" && ls.get("city").value !== "all" ? ls.get("city").id : "0");

  const [dateRange, setDateRange] = useState([
    {
      startDate: initialStartDate,
      endDate: initialEndDate,
      key: "selection",
    },
  ]);
  const [smdId, setSmdId] = useState(initialSmdId);
  const [provinceId, setProvinceId] = useState(initialProvinceId);
  const [cityId, setCityId] = useState(initialCityId);

  // Pagination state from URL
  const initialPage = searchParams.get("page") ? Number(searchParams.get("page")) : 0;
  const initialPageSize = searchParams.get("pageSize") ? Number(searchParams.get("pageSize")) : 10;
  const [paginationModel, setPaginationModel] = usePaginationState({
    page: initialPage,
    pageSize: initialPageSize,
  });

  // State for row details modal
  const [rowOpen, setRowOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const openRowDialog = (row) => {
    setSelectedRow(row);
    setRowOpen(true);
  };
  const closeRowDialog = () => setRowOpen(false);

  // --- Update URL params when filters/pagination change ---
  const updateUrlParams = (startDate, endDate, page, pageSize, smdId, provinceId, cityId) => {
    const params = {};
    if (startDate) params.startDate = formatter("SQLDate", startDate);
    if (endDate) params.endDate = formatter("SQLDate", endDate);
    if (page !== undefined) params.page = page;
    if (pageSize !== undefined) params.pageSize = pageSize;
    if (smdId) params.smdId = smdId;
    if (provinceId) params.provinceId = provinceId;
    if (cityId) params.cityId = cityId;
    setSearchParams(params);
  };

  // --- Fetch notes using filters from URL params ---
  const buildFilters = () => ({
    smdId: searchParams.get("smdId") || "0",
    provinceId: searchParams.get("provinceId") || "0",
    cityId: searchParams.get("cityId") || "0",
    dateFrom: searchParams.get("startDate") || format(initialStartDate, "yyyy-MM-dd"),
    dateTo: searchParams.get("endDate") || format(initialEndDate, "yyyy-MM-dd"),
    // Add more filters from searchParams if needed
  });

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const res = await getNotes(buildFilters());
      const list = res.data.notes || [];
      setNotes(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error fetching visitation notes:", error);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  };

  // --- Sync state with URL params ---
  useEffect(() => {
    // Update dateRange and pagination from URL params
    const startDate = searchParams.get("startDate")
      ? new Date(searchParams.get("startDate"))
      : subMonths(new Date(), 1);
    const endDate = searchParams.get("endDate")
      ? new Date(searchParams.get("endDate"))
      : new Date();
    setDateRange([{ ...dateRange[0], startDate, endDate }]);

    setSmdId(searchParams.get("smdId") || "0");
    setProvinceId(searchParams.get("provinceId") || "0");
    setCityId(searchParams.get("cityId") || "0");

    const page = searchParams.get("page") ? Number(searchParams.get("page")) : 0;
    const pageSize = searchParams.get("pageSize") ? Number(searchParams.get("pageSize")) : 10;
    setPaginationModel({ page, pageSize });

    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // --- Handlers that update URL params ---
  const handleDateApply = () => {
    setShowDatePicker(false);
    updateUrlParams(
      dateRange[0].startDate,
      dateRange[0].endDate,
      paginationModel.page,
      paginationModel.pageSize,
      smdId,
      provinceId,
      cityId
    );
  };

  // When pagination changes, update URL
  const handlePaginationChange = (model) => {
    setPaginationModel(model);
    updateUrlParams(
      dateRange[0].startDate,
      dateRange[0].endDate,
      model.page,
      model.pageSize,
      smdId,
      provinceId,
      cityId
    );
  };

  // When filter changes (e.g. from dropdowns), update URL
  const handleSmdChange = (newSmdId) => {
    setSmdId(newSmdId);
    updateUrlParams(
      dateRange[0].startDate,
      dateRange[0].endDate,
      paginationModel.page,
      paginationModel.pageSize,
      newSmdId,
      provinceId,
      cityId
    );
  };
  const handleProvinceChange = (newProvinceId) => {
    setProvinceId(newProvinceId);
    updateUrlParams(
      dateRange[0].startDate,
      dateRange[0].endDate,
      paginationModel.page,
      paginationModel.pageSize,
      smdId,
      newProvinceId,
      cityId
    );
  };
  const handleCityChange = (newCityId) => {
    setCityId(newCityId);
    updateUrlParams(
      dateRange[0].startDate,
      dateRange[0].endDate,
      paginationModel.page,
      paginationModel.pageSize,
      smdId,
      provinceId,
      newCityId
    );
  };

  const rows = useMemo(() => {
    return (notes || []).map((item, index) => {
      const id = item?.id ?? item?.visitationId ?? item?.visitation_id ?? index;
      const date = item?.date ?? item?.visit_date ?? item?.created_at ?? "-";
      const store = item?.storeName ?? item?.store_name ?? item?.store ?? "-";
      const user = item?.userName ?? item?.sales_name ?? item?.user ?? "-";
      const activation = item?.activation ?? "-";
      const note = item?.notes ?? "-";
      const competitor = item?.competitor_activity ?? item?.competitorActivity ?? "-";
      return {
        id,
        date,
        store,
        user,
        notes: note,
        activation,
        competitor_activity: competitor,
      };
    });
  }, [notes]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      [r.date, r.store, r.user, r.notes, r.activation, r.competitor_activity]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [rows, search]);

  const columns = [
    { field: "date", headerName: "Tanggal", width: 140, valueGetter: (p) => {
      try { return p.row.date ? format(new Date(p.row.date), "yyyy-MM-dd") : "-"; } catch { return p.row.date || "-"; }
    } },
    { field: "store", headerName: "Toko", flex: 1, minWidth: 200, renderCell: (params) => (
      <Box
        sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        title={String(params.value ?? "")}
      >
        {String(params.value ?? "-")}
      </Box>
    ) },
    { field: "user", headerName: "Sales", width: 160 },
    { field: "notes", headerName: "Catatan", flex: 1, minWidth: 240, renderCell: (params) => (
      <Box
        sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        title={String(params.value ?? "")}
      >
        {String(params.value ?? "-")}
      </Box>
    ) },
    { field: "activation", headerName: "Aktivasi", flex: 1, minWidth: 240, renderCell: (params) => (
      <Box
        sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        title={String(params.value ?? "")}
      >
        {String(params.value ?? "-")}
      </Box>
    ) },
    { field: "competitor_activity", headerName: "Aktivitas Kompetitor", flex: 1, minWidth: 240, renderCell: (params) => (
      <Box
        sx={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        title={String(params.value ?? "")}
      >
        {String(params.value ?? "-")}
      </Box>
    ) },
  ];

  const excelColumns = [
    { field: "date", headerName: "Tanggal", width: 140, valueGetter: (p) => {
      try { return p.row.date ? format(new Date(p.row.date), "yyyy-MM-dd") : "-"; } catch { return p.row.date || "-"; }
    } },
    { field: "store", headerName: "Toko", width: 200 },
    { field: "user", headerName: "Sales", width: 160 },
    { field: "notes", headerName: "Catatan", width: 240 },
    { field: "activation", headerName: "Aktivasi", width: 240 },
    { field: "competitor_activity", headerName: "Aktivitas Kompetitor", width: 240 },
  ];


  return (
    <div>
      <CCard>
        <CCardHeader>
          <CRow className="align-items-center">
            <CCol xs={12} className="mb-2">
              <h4 className="mb-0">Catatan Kunjungan</h4>
            </CCol>
          </CRow>
          <CRow className="align-items-center g-2">
            <CCol xs={12} lg={9}>
              <SalesPicker onFilterChange={fetchNotes} />
            </CCol>
            <CCol xs={12} lg={3}>
              <CFormInput
                type="text"
                placeholder="Cari catatan, toko, atau sales..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </CCol>
          </CRow>
          <CRow className="align-items-center g-2 mt-2">
            <CCol xs={8} lg={9}>
              <CButton color="primary" onClick={() => setShowDatePicker((v) => !v)}>
                {`${format(dateRange[0].startDate, "dd MMM yyyy")} – ${format(dateRange[0].endDate, "dd MMM yyyy")}`}
              </CButton>
            </CCol>
            <CCol xs={4} lg={3} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <DownloadExcel
                  rows={filteredRows}
                  columns={excelColumns}
                  excelName="Catatan Kunjungan"
                  dateFrom={`${format(dateRange[0].startDate, "dd MMM yyyy")}`}
                  dateTo={`${format(dateRange[0].endDate, "dd MMM yyyy")}`}
                  config={{ width: '30' }}
                  // extra={{id:1, merchantName: `Tanggal ${moment(formatter("SQLDate", dateRange[0].startDate)).format('D')} sampai ${moment(formatter("SQLDate", dateRange[0].endDate)).format('D')}`}}
                  dateFormat={["B"]}
                  // conditional={{
                  //   ref: `C4:L${monthlyExcel.length + 3}`,
                  //   rules: [
                  //     {
                  //       type: 'cellIs',
                  //       operator: 'equal',
                  //       formulae: ['=0'],
                  //       style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FF000000' } } },
                  //     },
                  //     {
                  //       type: 'cellIs',
                  //       operator: 'greaterThan',
                  //       formulae: ['=0'],
                  //       style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFC6EFCE' } } }, // green
                  //     },
                  //     {
                  //       type: 'cellIs',
                  //       operator: 'lessThan',
                  //       formulae: ['=0'],
                  //       style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFC5C5' } } }, // red
                  //     },
                  //   ],
                  // }}
                  border={{
                    ref: `A1:L${filteredRows.length +3}`,
                    rules: [
                      {
                        type: 'containsText',
                        operator: 'notContainsErrors',
                        style: {
                          border: {
                            top: { style: 'thin', color: { argb: 'FF000000' } },
                            bottom: { style: 'thin', color: { argb: 'FF000000' } },
                            left: { style: 'thin', color: { argb: 'FF000000' } },
                            right: { style: 'thin', color: { argb: 'FF000000' } },
                          },
                        },
                      },
                      {
                        type: 'expression',
                        formulae: ['MOD(ROW(),2)=0'],
                        style: {fill: {type: 'pattern', pattern: 'solid', bgColor: {argb: 'FFCDCDCD'}}},
                      }
                    ],
                  }}
                />
              </CCol>
          </CRow>
        </CCardHeader>
        {showDatePicker && (
          <CCardBody>
            <DateRangePicker
              onChange={(item) => setDateRange([item.selection])}
              showSelectionPreview
              moveRangeOnFirstSelection={false}
              months={2}
              ranges={dateRange}
              direction="horizontal"
            />
            <div className="mt-2">
              <CButton color="secondary" className="me-2" onClick={() => setShowDatePicker(false)}>
                Batal
              </CButton>
              <CButton color="primary" onClick={handleDateApply}>
                Terapkan
              </CButton>
            </div>
          </CCardBody>
        )}
        <CCardBody>
          {filteredRows.length ? (
            <DataTable
              columns={columns}
              rows={filteredRows}
              loading={loading}
              autoHeight
              density="compact"
              onRowClick={(params) => openRowDialog(params.row)}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
            />
          ) : (
            <div className="text-center text-muted">
              <CBadge color="secondary">Tidak ada catatan kunjungan</CBadge>
            </div>
          )}
        </CCardBody>
      </CCard>
     <CModal visible={rowOpen} onClose={closeRowDialog} size="lg">
       <CModalHeader onClose={closeRowDialog}>
         <h5 className="modal-title mb-0">Detail Catatan Kunjungan</h5>
       </CModalHeader>
       <CModalBody>
         {selectedRow && (
           <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
             <div><strong>Tanggal:</strong> {(() => { try { return selectedRow.date ? format(new Date(selectedRow.date), "yyyy-MM-dd") : "-"; } catch { return selectedRow.date || "-"; } })()}</div>
             <div><strong>Toko:</strong> {selectedRow.store ?? "-"}</div>
             <div><strong>Sales:</strong> {selectedRow.user ?? "-"}</div>
             <div><strong>Catatan:</strong> {selectedRow.notes ?? "-"}</div>
             <div><strong>Aktivasi:</strong> {selectedRow.activation ?? "-"}</div>
             <div><strong>Aktivitas Kompetitor:</strong> {selectedRow.competitor_activity ?? "-"}</div>
           </div>
         )}
       </CModalBody>
       <CModalFooter>
         {selectedRow && (
           <CButton color="primary" onClick={() => navigate(`/visitations/details/${selectedRow.id}`)}>
             Lihat Detail
           </CButton>
         )}
         <CButton color="secondary" onClick={closeRowDialog}>Tutup</CButton>
       </CModalFooter>
     </CModal>
    </div>
  );
}

export default VisitationNotes;
